import { useEffect } from "react";

import {
    Box,
    Container,
    Grid,
    Card,
    Typography,
    IconButton,
    Chip,
} from "@mui/material";

import {
    ArrowForwardRounded,
    ApartmentRounded,
    SchoolRounded,
    MenuBookRounded,
    DescriptionRounded,
    WorkspacePremiumRounded,
    GavelRounded,
    RuleRounded,
} from "@mui/icons-material";

import { useDocumentosPublicos } from "../../hooks/UseDocumentos";

const configuracionTipos = {
    reglamento: {
        color: "#C1121F",
        icon: <GavelRounded />,
        label: "Reglamento",
    },

    manual: {
        color: "#18A999",
        icon: <MenuBookRounded />,
        label: "Manual",
    },

    protocolo: {
        color: "#757575",
        icon: <DescriptionRounded />,
        label: "Protocolo",
    },

    institucional: {
        color: "#2D4E73",
        icon: <ApartmentRounded />,
        label: "Institucional",
    },
};

export const ReglasGrid = () => {

    const backendURL = process.env.REACT_APP_BACKEND_URL;


    const {
        documentos,
        loading,
        error,
        cargarDocumentosPublicos,
    } = useDocumentosPublicos();

    useEffect(() => {
        cargarDocumentosPublicos();
    }, []);

    /*
    |--------------------------------------------------------------------------
    | Preparar documentos
    |--------------------------------------------------------------------------
    */

    const documentosFormateados = Array.isArray(documentos)
        ? documentos.map((documento) => {

            const tipo = documento.tipo?.toLowerCase();

            const configuracion =
                configuracionTipos[tipo] ||
                {
                    color: "#2D4E73",
                    icon: <DescriptionRounded />,
                    label: documento.tipo || "Documento",
                };

            return {
                ...documento,

                titulo: documento.titulo,

                descripcion:
                    documento.descripcion ||
                    "Documento institucional del establecimiento.",

                categoria: configuracion.label,

                color: configuracion.color,

                icon: configuracion.icon,
            };
        })
        : [];

    /*
    |--------------------------------------------------------------------------
    | Loading
    |--------------------------------------------------------------------------
    */

    if (loading) {
        return (
            <Container maxWidth="xl">
                <Box
                    sx={{
                        py: 8,
                        textAlign: "center",
                    }}
                >
                    <Typography
                        sx={{
                            color: "#777",
                        }}
                    >
                        Cargando documentos...
                    </Typography>
                </Box>
            </Container>
        );
    }

    /*
    |--------------------------------------------------------------------------
    | Error
    |--------------------------------------------------------------------------
    */

    if (error) {
        return (
            <Container maxWidth="xl">
                <Box
                    sx={{
                        py: 8,
                        textAlign: "center",
                    }}
                >
                    <Typography
                        sx={{
                            color: "#C1121F",
                            fontWeight: 600,
                        }}
                    >
                        No fue posible cargar los documentos.
                    </Typography>
                </Box>
            </Container>
        );
    }

    /*
    |--------------------------------------------------------------------------
    | Render
    |--------------------------------------------------------------------------
    */

    return (
        <Container
            maxWidth="xl"
            sx={{
                py: 5,
            }}
        >

            <Grid
                container
                spacing={2.5}
            >

                {documentosFormateados.map((item) => (

                    <Grid
                        size={{
                            xs: 12,
                            sm: 6,
                            lg: 4,
                            xl: 3,
                        }}
                        key={item.id}
                    >

                        <Card
                            onClick={() => {
        if (item.archivo) {
            const archivoURL = `${backendURL}/${item.archivo}`;
            window.open(archivoURL, "_blank");
        }
    }}
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                p: 2.5,
                                borderRadius: "22px",
                                borderLeft:
                                    `5px solid ${item.color}`,
                                boxShadow:
                                    "0 5px 18px rgba(0,0,0,.05)",
                                transition: ".35s",
                                cursor: "pointer",
                                height: "100%",

                                "&:hover": {
                                    transform:
                                        "translateY(-6px)",

                                    boxShadow:
                                        "0 18px 40px rgba(0,0,0,.10)",

                                    "& .iconBox": {
                                        transform:
                                            "scale(1.08)",
                                    },

                                    "& .arrow": {
                                        bgcolor:
                                            item.color,
                                        color: "#fff",
                                        transform:
                                            "rotate(-45deg)",
                                    },
                                },
                            }}
                        >

                            {/* ICONO */}

                            <Box
                                className="iconBox"
                                sx={{
                                    width: 62,
                                    height: 62,
                                    minWidth: 62,
                                    borderRadius: "50%",
                                    bgcolor: item.color,
                                    color: "#fff",
                                    display: "flex",
                                    justifyContent:
                                        "center",
                                    alignItems: "center",
                                    transition: ".35s",

                                    "& svg": {
                                        fontSize: 30,
                                    },
                                }}
                            >
                                {item.icon}
                            </Box>

                            {/* TEXTO */}

                            <Box
                                sx={{
                                    ml: 2,
                                    flex: 1,
                                    minWidth: 0,
                                }}
                            >

                                <Chip
                                    label={item.categoria}
                                    size="small"
                                    sx={{
                                        mb: 1,
                                        bgcolor:
                                            `${item.color}15`,
                                        color:
                                            item.color,
                                        fontWeight: 700,
                                        fontSize: 11,
                                    }}
                                />

                                <Typography
                                    sx={{
                                        fontWeight: 700,
                                        color: "#1B2A41",
                                        fontSize: "1rem",
                                        lineHeight: 1.3,
                                    }}
                                >
                                    {item.titulo}
                                </Typography>

                                <Typography
                                    sx={{
                                        mt: 0.6,
                                        color: "#777",
                                        fontSize: ".88rem",
                                        lineHeight: 1.5,

                                        display:
                                            "-webkit-box",
                                        WebkitLineClamp: 2,
                                        WebkitBoxOrient:
                                            "vertical",
                                        overflow:
                                            "hidden",
                                    }}
                                >
                                    {item.descripcion}
                                </Typography>

                            </Box>

                            {/* FLECHA */}

                            <IconButton
                                className="arrow"
                                sx={{
                                    ml: 2,
                                    transition: ".35s",
                                    border:
                                        "1px solid #ECECEC",
                                    width: 42,
                                    height: 42,
                                    flexShrink: 0,
                                }}
                            >
                                <ArrowForwardRounded />
                            </IconButton>

                        </Card>

                    </Grid>

                ))}

            </Grid>

            {/* SIN DOCUMENTOS */}

            {!documentosFormateados.length && (
                <Box
                    sx={{
                        py: 8,
                        textAlign: "center",
                    }}
                >
                    <DescriptionRounded
                        sx={{
                            fontSize: 50,
                            color: "#CBD5E1",
                        }}
                    />

                    <Typography
                        sx={{
                            mt: 1,
                            fontWeight: 700,
                            color: "#475569",
                        }}
                    >
                        No hay documentos disponibles
                    </Typography>
                </Box>
            )}

        </Container>
    );
};